import React, { useState, useEffect } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getStripe } from '../../stripe/client';
import { stripeApi } from '../../firebase/functionsService';
import { useAuth } from '../../contexts/AuthContext';

// ── Ytre komponent: henter SetupIntent og wrapper Stripe Elements ─────────────
export default function PaymentSetup({ onSuccess }) {
  const { userDoc } = useAuth();
  const [clientSecret, setClientSecret] = useState(null);
  const [error, setError]               = useState('');
  const stripePromise = userDoc ? getStripe(userDoc.isTestAccount ?? false) : null;

  useEffect(() => {
    if (!stripePromise) return;
    stripeApi.createSetupIntent()
      .then(({ clientSecret }) => setClientSecret(clientSecret))
      .catch(() => setError('Kunne ikke starte betalingsoppsett. Prøv igjen.'));
  }, [stripePromise]);

  // Vent til brukerdokumentet er lastet — isTestAccount avgjør hvilken Stripe-nøkkel
  // som skal brukes, og å gjette "false" (live) mens dette lastes kan sende en
  // testkonto til live Stripe.
  if (!userDoc) return <p style={styles.loading}>Laster…</p>;

  if (!stripePromise) {
    const envVar  = userDoc.isTestAccount ? 'REACT_APP_STRIPE_PUBLISHABLE_KEY_TEST' : 'REACT_APP_STRIPE_PUBLISHABLE_KEY_LIVE';
    const envFile = userDoc.isTestAccount ? '.env.local' : '.env.production';
    return (
      <div style={styles.notice}>
        ⚙️ Stripe er ikke konfigurert — legg til <code>{envVar}</code> i {envFile}
      </div>
    );
  }

  if (error) return <p style={styles.error}>{error}</p>;
  if (!clientSecret) return <p style={styles.loading}>Laster betalingsoppsett…</p>;

  return (
    <Elements stripe={stripePromise} options={{ clientSecret, locale: 'nb' }}>
      <PaymentForm onSuccess={onSuccess} />
    </Elements>
  );
}

// ── Indre komponent: Stripe Elements-skjema ────────────────────────────────────
function PaymentForm({ onSuccess }) {
  const stripe   = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    if (!stripe || !elements) return;
    setLoading(true);
    setError('');

    const { error: stripeError, setupIntent } = await stripe.confirmSetup({
      elements,
      redirect: 'if_required',
      confirmParams: {
        return_url: `${window.location.origin}/orderer`,
      },
    });

    if (stripeError) {
      setError(stripeError.message);
      setLoading(false);
      return;
    }

    // Lagre betalingsmetode som default
    if (setupIntent?.payment_method) {
      try {
        await stripeApi.saveDefaultPaymentMethod(setupIntent.payment_method);
        onSuccess?.();
      } catch {
        setError('Kortinformasjon ble lagret, men vi kunne ikke sette det som standard. Prøv igjen.');
      }
    }

    setLoading(false);
  }

  return (
    <form onSubmit={handleSubmit} style={styles.form}>
      <PaymentElement />
      {error && <p style={styles.error}>{error}</p>}
      <button type="submit" style={styles.btn} disabled={loading || !stripe}>
        {loading ? 'Lagrer…' : 'Lagre betalingskort'}
      </button>
    </form>
  );
}

const styles = {
  form:    { display: 'flex', flexDirection: 'column', gap: 16, marginTop: 8 },
  btn:     { padding: '12px', borderRadius: 6, border: 'none', background: '#1a1a2e', color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer' },
  error:   { color: '#c0392b', fontSize: 13, background: '#ffeaea', padding: '8px 12px', borderRadius: 6 },
  loading: { color: '#999', fontSize: 14 },
  notice:  { background: '#fff8e6', border: '1px solid #fde89a', borderRadius: 8, padding: '12px 16px', fontSize: 13, color: '#7d5a00' },
};
